import {
  useBusinessEntitiesOfCompany,
  useCompanyData,
  useStockClasses,
} from '@commenda/api';
import {
  fetcher,
  formatDirectorName,
  SerializedStakeholder,
  StakeholderRelationshipTypeMap,
  validateEmail,
} from '@commenda/utils';
import {
  Currency,
  StakeholderRelationshipType,
  StakeholderType,
  TransactionType,
} from '@prisma/client';
import { Country as CountryLib } from 'country-state-city';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import {
  FieldWrapper,
  Modal,
  PrimaryButton,
  RadioInput,
  SelectInput,
  TextInput,
} from '../../../components';

type AddStakeholderModalProps = {
  isAddStakeholderModalShown: boolean;
  toggleAddStakeholderModalVisibility: () => void;
  token: string;
  mutate: () => void;
  setNotification: (arg0) => void;
  currency: Currency;
  stakeholders: SerializedStakeholder[];
  businessEntityId?: number;
  companyId?: number;
};

const AddStakeholderModal = ({
  isAddStakeholderModalShown,
  toggleAddStakeholderModalVisibility,
  token,
  mutate,
  stakeholders,
  setNotification,
  currency,
  businessEntityId: propBusinessEntityId,
  companyId: propCompanyId,
}: AddStakeholderModalProps) => {
  const router = useRouter();

  const { id: routerId, companyId: routerCompanyId } = router.query;

  const id = propBusinessEntityId || +(routerId as string);
  const companyId = propCompanyId || +(routerCompanyId as string);

  const { data } = useCompanyData(companyId, token);

  const { data: businessEntities, error: businessEntitiesError } =
    useBusinessEntitiesOfCompany(companyId);

  const { directors } = data || {};

  const [today, setToday] = useState('');

  const directorOptions =
    directors?.map((director) => ({
      displayedValue: formatDirectorName(director),
      value: director.id,
    })) || [];

  const corporationOptions =
    businessEntities?.entities
      ?.map((corporation) => {
        if (
          corporation.id !== id &&
          corporation.generalCorporationInfo?.legalName &&
          corporation.generalCorporationInfo.legalName.trim()
        ) {
          return {
            displayedValue: corporation.generalCorporationInfo.legalName,
            value: corporation.id,
          };
        }
        return null;
      })
      .filter(Boolean) || [];

  const { data: stockClasses } = useStockClasses(id);

  const serializedStockClassesOptions =
    stockClasses?.map((stockClass) => ({
      displayedValue: stockClass.name,
      value: stockClass.id,
    })) || [];

  const [stakeholderType, setStakeholderType] = useState<StakeholderType>(
    StakeholderType.INDIVIDUAL
  );

  const [selectedDirectorId, setSelectedDirectorId] = useState(null);
  const [selectedCorporationId, setSelectedCorporationId] = useState(null);
  const [nationality, setNationality] = useState(null);
  const [selectedRelationshipType, setSelectedRelationshipType] =
    useState(null);

  const [basicInformation, setBasicInformation] = useState({
    name: '',
    email: '',
  });

  const [selectedStockClassId, setSelectedStockClassId] = useState(null);

  const [date, setDate] = useState('');
  const [noOfSharesTransferred, setNoOfSharesTransferred] = useState(null);
  const [perSharePrice, setPerSharePrice] = useState(null);

  const handleAddStakeholder = async () => {
    if (!stakeholderType) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please select a stakeholder type.',
      });
      return false;
    }

    if (!selectedRelationshipType) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please select a stakeholder relationship type.',
      });
      return false;
    }

    if (stakeholderType === StakeholderType.INDIVIDUAL) {
      if (selectedDirectorId === 'add_new') {
        if (!basicInformation.name) {
          setNotification({
            show: true,
            valid: false,
            title: 'Error',
            message: 'Please enter a valid name.',
          });
          return false;
        }

        const isEmailValid = validateEmail(basicInformation.email);
        const doesEmailAlreadyExists = stakeholders?.find((stakeholder) => {
          return stakeholder.email === basicInformation.email;
        });

        if (!isEmailValid) {
          setNotification({
            show: true,
            valid: false,
            title: 'Error',
            message: 'Please enter a valid email.',
          });
          return false;
        }

        if (!!doesEmailAlreadyExists) {
          setNotification({
            show: true,
            valid: false,
            title: 'Error',
            message:
              'Stakeholder with this email already exists, please enter another email.',
          });
          return false;
        }

        if (!nationality) {
          setNotification({
            show: true,
            valid: false,
            title: 'Error',
            message: 'Please select Nationality.',
          });
          return false;
        }
      } else if (!selectedDirectorId) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'Please select a director.',
        });
        return false;
      }
    } else if (!selectedCorporationId) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please select a corporation.',
      });
      return false;
    }

    if (!selectedStockClassId) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please select a share class type.',
      });
      return false;
    }

    if (!noOfSharesTransferred) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please enter number of shares.',
      });
      return false;
    }

    if (!perSharePrice && noOfSharesTransferred > 0) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please enter per share price.',
      });
      return false;
    }

    const selectedStockClass = stockClasses.find(
      (stockClass) => stockClass.id === selectedStockClassId
    );

    const numberOfSharesLeftForSelectedStockClass =
      selectedStockClass.authorizedShares -
      selectedStockClass.outstandingShares;

    if (noOfSharesTransferred > numberOfSharesLeftForSelectedStockClass) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Share Class does not have enough shares.',
      });
      return false;
    }

    const defaultRequestBody = {
      corporationIdToBuySharesIn: id,
      type: stakeholderType,
      relationshipType: selectedRelationshipType,
      nationality,
      companyId: companyId,
      holdingSpecificFields: {
        transactionInformation: {
          stockClassId: selectedStockClassId,
          type: TransactionType.TX_STOCK_ISSUANCE,
          quantity: +noOfSharesTransferred,
          sharePrice: { currency, amount: +perSharePrice },
          ...(date ? { date } : {}),
        },
      },
    };

    const requestBody = {
      ...(stakeholderType === StakeholderType.INDIVIDUAL
        ? selectedDirectorId === 'add_new'
          ? {
              additionalInformation: {
                primaryContact: {
                  name: basicInformation.name,
                  emails: [basicInformation.email],
                },
                type: StakeholderType.INDIVIDUAL,
              },
              ...defaultRequestBody,
            }
          : { directorId: selectedDirectorId, ...defaultRequestBody }
        : selectedCorporationId === 'add_new'
          ? {
              additionalInformation: {
                primaryContact: {
                  name: basicInformation.name,
                  emails: [basicInformation.email],
                },
                type: StakeholderType.INSTITUTION,
              },
              ...defaultRequestBody,
            }
          : {
              corporationId: selectedCorporationId,
              ...defaultRequestBody,
            }),
    };

    const response = await fetcher(
      'capTable/stakeholding',
      { method: 'POST', body: requestBody },
      token
    );

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please try again later, or contact team Commenda.',
      });
      return false;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Successfully added the stakeholder',
    });

    await mutate();

    toggleAddStakeholderModalVisibility();

    setDate(null);
    setNoOfSharesTransferred(null);
    setPerSharePrice(null);
    setSelectedDirectorId(null);
    setSelectedCorporationId(null);
    setSelectedRelationshipType(null);
    setSelectedDirectorId(null);
    setSelectedCorporationId(null);
    setSelectedStockClassId(null);
    setBasicInformation({
      name: '',
      email: '',
    });
    setNationality(null);

    return true;
  };

  const countryOptions = CountryLib.getAllCountries().map((country) => {
    return { value: country.isoCode, displayedValue: country.name };
  });

  useEffect(() => {
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().substring(0, 10);
    setToday(formattedDate);
  }, []);

  return (
    <Modal
      isShown={isAddStakeholderModalShown}
      hide={toggleAddStakeholderModalVisibility}
      headerText="Add Stakeholder"
    >
      <div className="flex flex-col gap-4">
        <FieldWrapper label="Type of Stakeholder" id="stakeholderType">
          <div className="flex gap-4">
            <RadioInput
              label="Individual"
              name="stakeholderType"
              id="stakeholderType"
              checked={stakeholderType === StakeholderType.INDIVIDUAL}
              onCheck={() => setStakeholderType(StakeholderType.INDIVIDUAL)}
            />
            <RadioInput
              label="Entity"
              name="stakeholderType"
              id="stakeholderType"
              checked={stakeholderType === StakeholderType.INSTITUTION}
              onCheck={() => setStakeholderType(StakeholderType.INSTITUTION)}
            />
          </div>
        </FieldWrapper>
        {stakeholderType === StakeholderType.INSTITUTION ? (
          <>
            <FieldWrapper label="Choose Entity" id="chooseEntity">
              <SelectInput
                options={[
                  {
                    displayedValue: '+ Add Entity',
                    value: 'add_new',
                  },
                  ...corporationOptions,
                ]}
                value={selectedCorporationId}
                setValue={setSelectedCorporationId}
              />
            </FieldWrapper>
            {selectedCorporationId === 'add_new' && (
              <>
                <FieldWrapper label="Entity Name" id="name">
                  <TextInput
                    type="text"
                    placeholder="Entity Name"
                    id="name"
                    value={basicInformation.name}
                    setValue={(value) => {
                      // TODO: this needs to be fixed
                      setBasicInformation(
                        (prevInfo) =>
                          ({
                            name: value,
                            email: prevInfo.email,
                          }) as { name: string; email: string }
                      );
                    }}
                  />
                </FieldWrapper>
                <FieldWrapper label="Email" id="email">
                  <TextInput
                    type="text"
                    placeholder="Email Address"
                    id="email"
                    value={basicInformation.email}
                    setValue={(value) => {
                      // TODO: fix this
                      setBasicInformation(
                        (prevInfo) =>
                          ({
                            email: value,
                            name: prevInfo.name,
                          }) as { name: string; email: string }
                      );
                    }}
                  />
                </FieldWrapper>
              </>
            )}
            <FieldWrapper label="Nationality" id="nationality">
              <SelectInput
                options={countryOptions}
                value={nationality}
                setValue={setNationality}
              />
            </FieldWrapper>
          </>
        ) : (
          <>
            <FieldWrapper label="Choose a Stakeholder" id="chooseStakeholder">
              <SelectInput
                options={[
                  {
                    displayedValue: '+ Add Stakeholder',
                    value: 'add_new',
                  },
                  ...directorOptions,
                ]}
                value={selectedDirectorId}
                setValue={setSelectedDirectorId}
              />
            </FieldWrapper>
            {selectedDirectorId === 'add_new' && (
              <>
                <FieldWrapper label="Stakeholder Name" id="name">
                  <TextInput
                    type="text"
                    placeholder="Name"
                    id="name"
                    value={basicInformation.name}
                    setValue={(value) => {
                      // TODO: this needs to be fixed
                      setBasicInformation(
                        (prevInfo) =>
                          ({
                            name: value,
                            email: prevInfo.email,
                          }) as { name: string; email: string }
                      );
                    }}
                  />
                </FieldWrapper>
                <FieldWrapper label="Email" id="email">
                  <TextInput
                    type="text"
                    placeholder="Email Address"
                    id="email"
                    value={basicInformation.email}
                    setValue={(value) => {
                      // TODO: fix this
                      setBasicInformation(
                        (prevInfo) =>
                          ({
                            email: value,
                            name: prevInfo.name,
                          }) as { name: string; email: string }
                      );
                    }}
                  />
                </FieldWrapper>
              </>
            )}
            <FieldWrapper label="Nationality" id="nationality">
              <SelectInput
                options={countryOptions}
                value={nationality}
                setValue={setNationality}
              />
            </FieldWrapper>
          </>
        )}
        <FieldWrapper
          label="Choose Relationship Type"
          id="chooseRelationshipType"
        >
          <SelectInput
            options={Object.keys(StakeholderRelationshipType).map((each) => ({
              displayedValue: StakeholderRelationshipTypeMap[each],
              value: each,
            }))}
            value={selectedRelationshipType}
            setValue={setSelectedRelationshipType}
          />
        </FieldWrapper>
        <FieldWrapper label="Choose Share Class" id="stockClass">
          <SelectInput
            options={serializedStockClassesOptions}
            value={selectedStockClassId}
            setValue={setSelectedStockClassId}
          />
        </FieldWrapper>

        <FieldWrapper label="Date of Transaction" id="date">
          <TextInput
            type="date"
            id="date"
            placeholder="YYYY-MM-DD"
            value={date}
            setValue={setDate}
            max={today}
          />
        </FieldWrapper>
        <FieldWrapper
          label="Number of Shares Transferred"
          id="noOfSharesTransferred"
        >
          <TextInput
            placeholder="Number of Shares Transferred"
            value={noOfSharesTransferred}
            setValue={setNoOfSharesTransferred}
            type="number"
            id="noOfSharesTransferred"
          />
        </FieldWrapper>
        <FieldWrapper label="Share Transfer Price" id="sharePerPrice">
          <TextInput
            id="shareTransferPrice"
            placeholder="Share Transfer Price"
            value={perSharePrice}
            setValue={setPerSharePrice}
            type="number"
          />
        </FieldWrapper>
        <div className="flex gap-4 self-end">
          <PrimaryButton onClick={handleAddStakeholder}>
            Add Stakeholder
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default AddStakeholderModal;
